// @ts-strict-ignore
import React, { useState } from 'react';
import {
  Badge,
  Button,
  ButtonSize,
  ButtonVariant,
  IconName,
  Text,
} from '@usealloy/component-library';

import {
  StyledCategoryContainer,
  StyledDeactivatedCategoryContainer,
  CategoryInfo,
  StyledPre,
  RouteActions,
} from './styles';
import RoutingServiceDropdown from './RoutingServiceDropdown';
import type { DataRoutingCategories } from '~/dashboard/views/workflows/service/dataRoutes/types';
import type { DataRoute } from '~/dashboard/views/workflows/store/engine/types';
import {
  addDeactivatedLegacyDataRoute,
  createDataRoute,
  removeDataRoute,
  updateDataRouteValue,
  setServiceRoutingCategoryNameInEditing,
} from '~/dashboard/views/workflows/store/redux/slice';
import { useAppDispatch } from '~/store/reduxHooks';

interface RoutingCategoryProps {
  mode: 'incoming' | 'outgoing';
  dataRoutingCategory: DataRoutingCategories;
  isEditing: boolean;
  isDynamicDataRoutingEnabled: boolean;
  isDeactivated?: boolean;
  isDisabled?: boolean;
}

export default function RoutingCategory({
  mode,
  dataRoutingCategory,
  isEditing,
  isDynamicDataRoutingEnabled,
  isDisabled = false,
  isDeactivated = false,
}: RoutingCategoryProps) {
  const dispatch = useAppDispatch();
  const [dataRouteForCategory, setDataRouteForCategory] = useState<DataRoute>(
    dataRoutingCategory.dataRoute,
  );

  const onClickEdit = () => {
    if (!isEditing) {
      dispatch(setServiceRoutingCategoryNameInEditing(dataRoutingCategory.category.name));
      return;
    }

    if (dataRoutingCategory.dataRoute) {
      // means this is editing existing data routing configuration
      dispatch(
        updateDataRouteValue({
          path: dataRouteForCategory.publicId,
          value: dataRouteForCategory,
        }),
      );
    } else if (dataRouteForCategory) {
      dispatch(
        createDataRoute({
          path: dataRouteForCategory.publicId,
          value: dataRouteForCategory,
          expressionPath: '',
        }),
      );
    }
    dispatch(setServiceRoutingCategoryNameInEditing(null));
  };

  const onClickRemove = () => {
    dispatch(
      removeDataRoute({
        id: dataRouteForCategory.publicId,
      }),
    );
    setDataRouteForCategory(null);
  };

  const onClickDeactivate = () => {
    dispatch(addDeactivatedLegacyDataRoute({
      mode,
      category: dataRoutingCategory.category,
      dataRoute: dataRouteForCategory,
    }));
    dispatch(removeDataRoute({
      id: dataRouteForCategory.publicId,
    }));
    setDataRouteForCategory(null);
  };

  const Container = isDeactivated ? StyledDeactivatedCategoryContainer : StyledCategoryContainer;

  return (
    <Container>
      <CategoryInfo>
        <Text as="span" size={2} weight={400} style={{ color: 'var(--cl-gray-700)' }}>
          Data
        </Text>
        <StyledPre>{dataRoutingCategory.category.name}</StyledPre>
      </CategoryInfo>

      <CategoryInfo>
        <Text as="span" size={2} weight={400} style={{ color: 'var(--cl-gray-700)' }}>
          Routed {mode === 'incoming' ? 'From' : 'To'}
        </Text>
        <RoutingServiceDropdown
          mode={mode}
          category={dataRoutingCategory.category}
          isEditing={!isDeactivated && isEditing}
          dataRouteForCategory={dataRouteForCategory}
          setDataRouteForCategory={setDataRouteForCategory}
        />
      </CategoryInfo>

      {isDeactivated ? (
        <Badge status="disabled">Deactivated</Badge>
      ) : (
        <RouteActions>
          {isDynamicDataRoutingEnabled ? (
            <Button
              variant={ButtonVariant.primary}
              size={ButtonSize.small}
              disabled={!dataRouteForCategory || isDisabled}
              onClick={onClickDeactivate}
            >
              Deactivate
            </Button>
          ) : (
            <>
              <Button
                icon={isEditing ? IconName.Approved : IconName.Edit}
                variant={ButtonVariant.tertiary}
                onClick={onClickEdit}
                disabled={isDisabled}
              />
              <Button
                icon={IconName.Trash}
                variant={ButtonVariant.tertiary}
                disabled={!dataRouteForCategory || isDisabled}
                onClick={onClickRemove}
              />
            </>
          )}
        </RouteActions>
      )}
    </Container>
  );
}
